import React, { useMemo, useState } from "react";

type Page =
  | "home"
  | "categories"
  | "capital-market"
  | "real-estate"
  | "taxes"
  | "pension"
  | "compound"
  | "dca"
  | "return"
  | "cagr"
  | "future-value"
  | "goal"
  | "million"
  | "dividend"
  | "capital-gains-tax"
  | "mortgage"
  | "loan"
  | "rental-yield"
  | "cashflow"
  | "downpayment"
  | "rent-vs-buy"
  | "purchase-tax"
  | "vat"
  | "rental-tax"
  | "inflation"
  | "gross-net"
  | "fire"
  | "monthly-income-capital"
  | "withdrawal"
  | "pension-future";

const palette = {
  bg: "#f5f7fb",
  card: "#ffffff",
  text: "#0f172a",
  subtext: "#64748b",
  primary: "#2563eb",
  line: "#e5e7eb",
};

const pageWrap: React.CSSProperties = {
  minHeight: "100vh",
  background:
    "radial-gradient(circle at 12% 10%, rgba(37,99,235,0.16), transparent 28%), radial-gradient(circle at 85% 15%, rgba(59,130,246,0.10), transparent 24%), radial-gradient(circle at 50% 100%, rgba(15,23,42,0.06), transparent 30%), linear-gradient(180deg, #ffffff 0%, #f5f7fb 100%)",
  fontFamily:
    '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Arial, sans-serif',
  direction: "rtl",
  color: palette.text,
};

const containerStyle: React.CSSProperties = {
  maxWidth: 1180,
  margin: "0 auto",
  padding: "32px 20px 60px",
};

const shellCardStyle: React.CSSProperties = {
  background: "rgba(255,255,255,0.92)",
  border: `1px solid ${palette.line}`,
  borderRadius: 30,
  boxShadow: "0 20px 60px rgba(15,23,42,0.08)",
};

const tileStyle: React.CSSProperties = {
  background: "#fff",
  padding: 28,
  borderRadius: 24,
  border: `1px solid ${palette.line}`,
  boxShadow: "0 10px 30px rgba(0,0,0,0.04)",
  cursor: "pointer",
  textAlign: "center",
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "15px 16px",
  borderRadius: 14,
  border: `1px solid ${palette.line}`,
  fontSize: 18,
  boxSizing: "border-box",
  textAlign: "right",
  direction: "rtl",
  background: "#fff",
};

function formatMoney(num: number) {
  if (!Number.isFinite(num)) return "0";
  return Math.round(num).toLocaleString("he-IL");
}

function formatMoney2(num: number) {
  if (!Number.isFinite(num)) return "0.00";
  return num.toLocaleString("he-IL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function safePercent(num: number) {
  if (!Number.isFinite(num)) return "0.00";
  return num.toFixed(2);
}

function BackButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: "10px 18px",
        borderRadius: 999,
        border: `1px solid ${palette.line}`,
        background: "#fff",
        cursor: "pointer",
        fontWeight: 600,
        marginBottom: 22,
      }}
    >
      חזרה
    </button>
  );
}

function PageShell({
  onBack,
  children,
}: {
  onBack: () => void;
  children: React.ReactNode;
}) {
  return (
    <div style={pageWrap}>
      <div style={containerStyle}>
        <BackButton onClick={onBack} />
        <div style={{ ...shellCardStyle, padding: 30 }}>{children}</div>
      </div>
    </div>
  );
}

function TitleBlock({
  eyebrow,
  title,
  subtitle,
}: {
  eyebrow?: string;
  title: string;
  subtitle?: string;
}) {
  return (
    <div style={{ marginBottom: 26 }}>
      {eyebrow && (
        <div
          style={{
            fontSize: 12,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: palette.primary,
            opacity: 0.75,
            marginBottom: 10,
            fontWeight: 700,
          }}
        >
          {eyebrow}
        </div>
      )}
      <h1
        style={{
          margin: 0,
          fontSize: 42,
          lineHeight: 1.05,
          letterSpacing: "-0.04em",
        }}
      >
        {title}
      </h1>
      {subtitle && (
        <p
          style={{
            marginTop: 14,
            marginBottom: 0,
            color: palette.subtext,
            fontSize: 18,
            lineHeight: 1.8,
            maxWidth: 780,
          }}
        >
          {subtitle}
        </p>
      )}
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div
        style={{
          marginBottom: 8,
          fontSize: 15,
          fontWeight: 600,
          color: palette.text,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

function TwoCol({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
        gap: 16,
      }}
    >
      {children}
    </div>
  );
}

function ResultCard({
  label,
  value,
  lines,
}: {
  label: string;
  value: string;
  lines?: string[];
}) {
  return (
    <div
      style={{
        marginTop: 28,
        background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
        border: `1px solid ${palette.line}`,
        borderRadius: 22,
        padding: 24,
        boxShadow: "0 10px 30px rgba(15,23,42,0.04)",
      }}
    >
      <div style={{ color: palette.subtext, fontSize: 15 }}>{label}</div>
      <div
        style={{
          fontSize: 40,
          fontWeight: 800,
          marginTop: 10,
          letterSpacing: "-0.03em",
        }}
      >
        {value}
      </div>
      {lines?.map((line, i) => (
        <div
          key={i}
          style={{ marginTop: 10, color: palette.subtext, fontSize: 17 }}
        >
          {line}
        </div>
      ))}
    </div>
  );
}

function CategoryPage({
  title,
  subtitle,
  backPage,
  setPage,
  items,
}: {
  title: string;
  subtitle: string;
  backPage: Page;
  setPage: (page: Page) => void;
  items: { title: string; desc: string; page: Page }[];
}) {
  return (
    <div style={pageWrap}>
      <div style={containerStyle}>
        <BackButton onClick={() => setPage(backPage)} />

        <div style={{ ...shellCardStyle, padding: 30 }}>
          <TitleBlock
            eyebrow="Built for better decisions"
            title={title}
            subtitle={subtitle}
          />

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
              gap: 20,
            }}
          >
            {items.map((item) => (
              <div
                key={item.page}
                onClick={() => setPage(item.page)}
                style={tileStyle}
              >
                <div
                  style={{ fontSize: 26, marginBottom: 10, fontWeight: 800 }}
                >
                  {item.title}
                </div>
                <div
                  style={{
                    color: palette.subtext,
                    fontSize: 16,
                    fontWeight: 500,
                    lineHeight: 1.7,
                  }}
                >
                  {item.desc}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ================= שוק ההון ================= */

function CompoundCalculator() {
  const [startAmount, setStartAmount] = useState(100000);
  const [monthlyDeposit, setMonthlyDeposit] = useState(2000);
  const [annualReturn, setAnnualReturn] = useState(8);
  const [years, setYears] = useState(20);

  const months = years * 12;
  const monthlyRate = annualReturn / 100 / 12;

  let total = startAmount;
  for (let i = 0; i < months; i++) {
    total = total * (1 + monthlyRate) + monthlyDeposit;
  }

  const invested = startAmount + monthlyDeposit * months;
  const profit = total - invested;

  return (
    <div>
      <TitleBlock
        eyebrow="Financial clarity starts here."
        title="מחשבון ריבית דריבית"
        subtitle="בדוק איך כסף גדל לאורך זמן כאשר הרווחים ממשיכים לעבוד בשבילך."
      />
      <TwoCol>
        <Field label="סכום התחלתי">
          <input
            type="number"
            value={startAmount}
            onChange={(e) => setStartAmount(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
        <Field label="הפקדה חודשית">
          <input
            type="number"
            value={monthlyDeposit}
            onChange={(e) => setMonthlyDeposit(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
        <Field label="תשואה שנתית (%)">
          <input
            type="number"
            value={annualReturn}
            onChange={(e) => setAnnualReturn(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
        <Field label="מספר שנים">
          <input
            type="number"
            value={years}
            onChange={(e) => setYears(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
      </TwoCol>
      <ResultCard
        label="סכום סופי"
        value={`₪${formatMoney(total)}`}
        lines={[
          `סך הפקדות: ₪${formatMoney(invested)}`,
          `רווח: ₪${formatMoney(profit)}`,
        ]}
      />
    </div>
  );
}

function DcaCalculator() {
  const [monthlyDeposit, setMonthlyDeposit] = useState(3000);
  const [annualReturn, setAnnualReturn] = useState(10);
  const [years, setYears] = useState(20);

  const months = years * 12;
  const monthlyRate = annualReturn / 100 / 12;

  let total = 0;
  for (let i = 0; i < months; i++) {
    total = total * (1 + monthlyRate) + monthlyDeposit;
  }

  const invested = monthlyDeposit * months;
  const profit = total - invested;

  return (
    <div>
      <TitleBlock
        eyebrow="Built for better decisions"
        title="מחשבון DCA"
        subtitle="גלה מה יכולה לעשות השקעה חודשית קבועה לאורך זמן."
      />
      <TwoCol>
        <Field label="הפקדה חודשית">
          <input
            type="number"
            value={monthlyDeposit}
            onChange={(e) => setMonthlyDeposit(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
        <Field label="תשואה שנתית (%)">
          <input
            type="number"
            value={annualReturn}
            onChange={(e) => setAnnualReturn(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
        <Field label="מספר שנים">
          <input
            type="number"
            value={years}
            onChange={(e) => setYears(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
      </TwoCol>
      <ResultCard
        label="סכום סופי"
        value={`₪${formatMoney(total)}`}
        lines={[
          `סך הפקדות: ₪${formatMoney(invested)}`,
          `רווח: ₪${formatMoney(profit)}`,
        ]}
      />
    </div>
  );
}

function ReturnCalculator() {
  const [startValue, setStartValue] = useState(100000);
  const [endValue, setEndValue] = useState(135000);

  const profit = endValue - startValue;
  const percent = startValue === 0 ? 0 : (profit / startValue) * 100;

  return (
    <div>
      <TitleBlock
        eyebrow="Built for better decisions"
        title="מחשבון תשואה"
        subtitle="חשב במהירות את אחוז הרווח או ההפסד על ההשקעה שלך."
      />
      <TwoCol>
        <Field label="שווי התחלתי">
          <input
            type="number"
            value={startValue}
            onChange={(e) => setStartValue(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
        <Field label="שווי סופי">
          <input
            type="number"
            value={endValue}
            onChange={(e) => setEndValue(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
      </TwoCol>
      <ResultCard
        label="תשואה"
        value={`${safePercent(percent)}%`}
        lines={[`רווח כספי: ₪${formatMoney(profit)}`]}
      />
    </div>
  );
}

function CagrCalculator() {
  const [startValue, setStartValue] = useState(100000);
  const [endValue, setEndValue] = useState(200000);
  const [years, setYears] = useState(5);

  const cagr =
    startValue > 0 && years > 0
      ? (Math.pow(endValue / startValue, 1 / years) - 1) * 100
      : 0;

  return (
    <div>
      <TitleBlock
        eyebrow="Built for better decisions"
        title="מחשבון CAGR"
        subtitle="חשב את התשואה השנתית הממוצעת לאורך כמה שנים."
      />
      <TwoCol>
        <Field label="שווי התחלתי">
          <input
            type="number"
            value={startValue}
            onChange={(e) => setStartValue(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
        <Field label="שווי סופי">
          <input
            type="number"
            value={endValue}
            onChange={(e) => setEndValue(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
        <Field label="מספר שנים">
          <input
            type="number"
            value={years}
            onChange={(e) => setYears(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
      </TwoCol>
      <ResultCard label="תשואה שנתית ממוצעת" value={`${safePercent(cagr)}%`} />
    </div>
  );
}

function FutureValueCalculator() {
  const [currentAmount, setCurrentAmount] = useState(500000);
  const [annualReturn, setAnnualReturn] = useState(8);
  const [years, setYears] = useState(15);

  const futureValue = currentAmount * Math.pow(1 + annualReturn / 100, years);

  return (
    <div>
      <TitleBlock
        eyebrow="Financial clarity starts here."
        title="כמה יהיה לי בעתיד"
        subtitle="בדוק כמה יהיה שווה סכום כסף בעתיד לפי תשואה שנתית."
      />
      <TwoCol>
        <Field label="סכום נוכחי">
          <input
            type="number"
            value={currentAmount}
            onChange={(e) => setCurrentAmount(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
        <Field label="תשואה שנתית (%)">
          <input
            type="number"
            value={annualReturn}
            onChange={(e) => setAnnualReturn(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
        <Field label="מספר שנים">
          <input
            type="number"
            value={years}
            onChange={(e) => setYears(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
      </TwoCol>
      <ResultCard label="שווי עתידי" value={`₪${formatMoney(futureValue)}`} />
    </div>
  );
}

function GoalCalculator() {
  const [goal, setGoal] = useState(1000000);
  const [startAmount, setStartAmount] = useState(100000);
  const [annualReturn, setAnnualReturn] = useState(8);
  const [years, setYears] = useState(15);

  const monthlyRate = annualReturn / 100 / 12;
  const months = years * 12;
  const futureStart = startAmount * Math.pow(1 + monthlyRate, months);

  const neededMonthly =
    months <= 0
      ? 0
      : monthlyRate === 0
      ? Math.max(0, (goal - startAmount) / months)
      : Math.max(
          0,
          ((goal - futureStart) * monthlyRate) /
            (Math.pow(1 + monthlyRate, months) - 1)
        );

  return (
    <div>
      <TitleBlock
        eyebrow="Built for better decisions"
        title="כמה צריך לחסוך ליעד"
        subtitle="חשב כמה צריך להפקיד כל חודש כדי להגיע ליעד שלך."
      />
      <TwoCol>
        <Field label="יעד">
          <input
            type="number"
            value={goal}
            onChange={(e) => setGoal(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
        <Field label="סכום התחלתי">
          <input
            type="number"
            value={startAmount}
            onChange={(e) => setStartAmount(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
        <Field label="תשואה שנתית (%)">
          <input
            type="number"
            value={annualReturn}
            onChange={(e) => setAnnualReturn(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
        <Field label="מספר שנים">
          <input
            type="number"
            value={years}
            onChange={(e) => setYears(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
      </TwoCol>
      <ResultCard
        label="הפקדה חודשית נדרשת"
        value={`₪${formatMoney(neededMonthly)}`}
      />
    </div>
  );
}

function MillionCalculator() {
  const [startAmount, setStartAmount] = useState(100000);
  const [monthlyDeposit, setMonthlyDeposit] = useState(3000);
  const [annualReturn, setAnnualReturn] = useState(8);

  const monthlyRate = annualReturn / 100 / 12;
  let total = startAmount;
  let months = 0;

  while (total < 1000000 && months < 12000) {
    total = total * (1 + monthlyRate) + monthlyDeposit;
    months++;
  }

  const years = Math.floor(months / 12);
  const remainMonths = months % 12;

  return (
    <div>
      <TitleBlock
        eyebrow="Financial clarity starts here."
        title="כמה זמן עד מיליון"
        subtitle="בדוק תוך כמה זמן אפשר להגיע למיליון."
      />
      <TwoCol>
        <Field label="סכום התחלתי">
          <input
            type="number"
            value={startAmount}
            onChange={(e) => setStartAmount(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
        <Field label="הפקדה חודשית">
          <input
            type="number"
            value={monthlyDeposit}
            onChange={(e) => setMonthlyDeposit(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
        <Field label="תשואה שנתית (%)">
          <input
            type="number"
            value={annualReturn}
            onChange={(e) => setAnnualReturn(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
      </TwoCol>
      <ResultCard
        label="זמן משוער"
        value={`${years} שנים ו-${remainMonths} חודשים`}
        lines={[`שווי סופי: ₪${formatMoney(total)}`]}
      />
    </div>
  );
}

function DividendCalculator() {
  const [shares, setShares] = useState(1000);
  const [dividendPerShare, setDividendPerShare] = useState(1.2);
  const [paymentsPerYear, setPaymentsPerYear] = useState(4);

  const yearlyDividend = shares * dividendPerShare * paymentsPerYear;
  const monthlyDividend = yearlyDividend / 12;

  return (
    <div>
      <TitleBlock
        eyebrow="Built for better decisions"
        title="מחשבון דיבידנד"
        subtitle="חשב הכנסה שנתית וחודשית מדיבידנדים."
      />
      <TwoCol>
        <Field label="מספר מניות">
          <input
            type="number"
            value={shares}
            onChange={(e) => setShares(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
        <Field label="דיבידנד למניה">
          <input
            type="number"
            value={dividendPerShare}
            onChange={(e) => setDividendPerShare(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
        <Field label="מספר חלוקות בשנה">
          <input
            type="number"
            value={paymentsPerYear}
            onChange={(e) => setPaymentsPerYear(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
      </TwoCol>
      <ResultCard
        label="דיבידנד שנתי"
        value={`₪${formatMoney2(yearlyDividend)}`}
        lines={[`דיבידנד חודשי ממוצע: ₪${formatMoney2(monthlyDividend)}`]}
      />
    </div>
  );
}

function CapitalGainsTaxCalculator() {
  const [buy, setBuy] = useState(100000);
  const [sell, setSell] = useState(140000);
  const [taxRate, setTaxRate] = useState(25);

  const profit = sell - buy;
  const tax = profit > 0 ? profit * (taxRate / 100) : 0;
  const netAfterTax = sell - tax;

  return (
    <div>
      <TitleBlock
        eyebrow="Built for better decisions"
        title="מס רווחי הון"
        subtitle="בדוק כמה מס משולם על רווח בהשקעה."
      />
      <TwoCol>
        <Field label="שווי קנייה">
          <input
            type="number"
            value={buy}
            onChange={(e) => setBuy(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
        <Field label="שווי מכירה">
          <input
            type="number"
            value={sell}
            onChange={(e) => setSell(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
        <Field label="אחוז מס (%)">
          <input
            type="number"
            value={taxRate}
            onChange={(e) => setTaxRate(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
      </TwoCol>
      <ResultCard
        label="מס לתשלום"
        value={`₪${formatMoney(tax)}`}
        lines={[
          `רווח: ₪${formatMoney(profit)}`,
          `נטו אחרי מס: ₪${formatMoney(netAfterTax)}`,
        ]}
      />
    </div>
  );
}

/* ================= נדל״ן ================= */

function MortgageCalculator() {
  const [loan, setLoan] = useState(1000000);
  const [rate, setRate] = useState(4);
  const [years, setYears] = useState(25);

  const monthlyRate = rate / 100 / 12;
  const months = years * 12;

  const payment =
    monthlyRate === 0
      ? loan / months
      : (loan * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -months));

  const totalPaid = payment * months;
  const totalInterest = totalPaid - loan;

  return (
    <div>
      <TitleBlock
        eyebrow="Financial clarity starts here."
        title="מחשבון משכנתא"
        subtitle="חשב החזר חודשי ועלות כוללת של משכנתא."
      />
      <TwoCol>
        <Field label="סכום הלוואה">
          <input
            type="number"
            value={loan}
            onChange={(e) => setLoan(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
        <Field label="ריבית שנתית (%)">
          <input
            type="number"
            value={rate}
            onChange={(e) => setRate(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
        <Field label="מספר שנים">
          <input
            type="number"
            value={years}
            onChange={(e) => setYears(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
      </TwoCol>
      <ResultCard
        label="החזר חודשי"
        value={`₪${formatMoney(payment)}`}
        lines={[
          `סך תשלום: ₪${formatMoney(totalPaid)}`,
          `סך ריבית: ₪${formatMoney(totalInterest)}`,
        ]}
      />
    </div>
  );
}

function LoanCalculator() {
  const [loan, setLoan] = useState(100000);
  const [rate, setRate] = useState(8);
  const [years, setYears] = useState(5);

  const monthlyRate = rate / 100 / 12;
  const months = years * 12;

  const payment =
    monthlyRate === 0
      ? loan / months
      : (loan * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -months));

  const totalPaid = payment * months;
  const totalInterest = totalPaid - loan;

  return (
    <div>
      <TitleBlock
        eyebrow="Built for better decisions"
        title="מחשבון הלוואה"
        subtitle="חשב החזר חודשי של הלוואה רגילה."
      />
      <TwoCol>
        <Field label="סכום הלוואה">
          <input
            type="number"
            value={loan}
            onChange={(e) => setLoan(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
        <Field label="ריבית שנתית (%)">
          <input
            type="number"
            value={rate}
            onChange={(e) => setRate(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
        <Field label="מספר שנים">
          <input
            type="number"
            value={years}
            onChange={(e) => setYears(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
      </TwoCol>
      <ResultCard
        label="החזר חודשי"
        value={`₪${formatMoney(payment)}`}
        lines={[
          `סך תשלום: ₪${formatMoney(totalPaid)}`,
          `סך ריבית: ₪${formatMoney(totalInterest)}`,
        ]}
      />
    </div>
  );
}

function RentalYieldCalculator() {
  const [price, setPrice] = useState(2000000);
  const [rent, setRent] = useState(5000);

  const yearlyRent = rent * 12;
  const yieldPercent = price === 0 ? 0 : (yearlyRent / price) * 100;

  return (
    <div>
      <TitleBlock
        eyebrow="Built for better decisions"
        title="תשואה על דירה"
        subtitle="בדוק את התשואה השנתית של הנכס."
      />
      <TwoCol>
        <Field label="מחיר הדירה">
          <input
            type="number"
            value={price}
            onChange={(e) => setPrice(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
        <Field label="שכירות חודשית">
          <input
            type="number"
            value={rent}
            onChange={(e) => setRent(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
      </TwoCol>
      <ResultCard
        label="תשואה שנתית"
        value={`${safePercent(yieldPercent)}%`}
        lines={[`שכירות שנתית: ₪${formatMoney(yearlyRent)}`]}
      />
    </div>
  );
}

function CashflowCalculator() {
  const [rent, setRent] = useState(5500);
  const [mortgage, setMortgage] = useState(3500);
  const [expenses, setExpenses] = useState(700);

  const monthlyCashflow = rent - mortgage - expenses;
  const yearlyCashflow = monthlyCashflow * 12;

  return (
    <div>
      <TitleBlock
        eyebrow="Financial clarity starts here."
        title="תזרים שכירות"
        subtitle="בדוק כמה נשאר לך בפועל מנכס כל חודש."
      />
      <TwoCol>
        <Field label="שכירות חודשית">
          <input
            type="number"
            value={rent}
            onChange={(e) => setRent(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
        <Field label="משכנתא חודשית">
          <input
            type="number"
            value={mortgage}
            onChange={(e) => setMortgage(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
        <Field label="הוצאות חודשיות">
          <input
            type="number"
            value={expenses}
            onChange={(e) => setExpenses(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
      </TwoCol>
      <ResultCard
        label="תזרים חודשי"
        value={`₪${formatMoney(monthlyCashflow)}`}
        lines={[`תזרים שנתי: ₪${formatMoney(yearlyCashflow)}`]}
      />
    </div>
  );
}

function DownpaymentCalculator() {
  const [price, setPrice] = useState(2000000);
  const [equityPercent, setEquityPercent] = useState(25);

  const equity = price * (equityPercent / 100);
  const mortgage = price - equity;

  return (
    <div>
      <TitleBlock
        eyebrow="Built for better decisions"
        title="הון עצמי נדרש"
        subtitle="בדוק כמה הון עצמי צריך להביא לעסקה."
      />
      <TwoCol>
        <Field label="מחיר הנכס">
          <input
            type="number"
            value={price}
            onChange={(e) => setPrice(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
        <Field label="אחוז הון עצמי (%)">
          <input
            type="number"
            value={equityPercent}
            onChange={(e) => setEquityPercent(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
      </TwoCol>
      <ResultCard
        label="הון עצמי"
        value={`₪${formatMoney(equity)}`}
        lines={[`משכנתא משוערת: ₪${formatMoney(mortgage)}`]}
      />
    </div>
  );
}

function RentVsBuyCalculator() {
  const [rent, setRent] = useState(5500);
  const [mortgage, setMortgage] = useState(6500);

  const diff = mortgage - rent;

  return (
    <div>
      <TitleBlock
        eyebrow="Financial clarity starts here."
        title="שכירות מול קנייה"
        subtitle="השווה בין החזר משכנתא לבין שכירות חודשית."
      />
      <TwoCol>
        <Field label="שכירות חודשית">
          <input
            type="number"
            value={rent}
            onChange={(e) => setRent(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
        <Field label="החזר משכנתא חודשי">
          <input
            type="number"
            value={mortgage}
            onChange={(e) => setMortgage(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
      </TwoCol>
      <ResultCard
        label="הפרש חודשי"
        value={`₪${formatMoney(diff)}`}
        lines={[diff > 0 ? "המשכנתא יקרה יותר" : "השכירות יקרה יותר או שווה"]}
      />
    </div>
  );
}

function PurchaseTaxCalculator() {
  const [price, setPrice] = useState(2000000);
  const [rate, setRate] = useState(8);

  const tax = price * (rate / 100);

  return (
    <div>
      <TitleBlock
        eyebrow="Built for better decisions"
        title="מס רכישה"
        subtitle="חישוב מהיר של מס רכישה משוער."
      />
      <TwoCol>
        <Field label="מחיר נכס">
          <input
            type="number"
            value={price}
            onChange={(e) => setPrice(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
        <Field label="אחוז מס (%)">
          <input
            type="number"
            value={rate}
            onChange={(e) => setRate(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
      </TwoCol>
      <ResultCard label="מס רכישה משוער" value={`₪${formatMoney(tax)}`} />
    </div>
  );
}

/* ================= מיסים ================= */

function VatCalculator() {
  const [amount, setAmount] = useState(1000);
  const [vatRate, setVatRate] = useState(18);

  const vat = amount * (vatRate / 100);
  const total = amount + vat;

  return (
    <div>
      <TitleBlock
        eyebrow="Financial clarity starts here."
        title="מחשבון מע״מ"
        subtitle="חשב מע״מ וסכום כולל."
      />
      <TwoCol>
        <Field label="סכום לפני מע״מ">
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
        <Field label="אחוז מע״מ (%)">
          <input
            type="number"
            value={vatRate}
            onChange={(e) => setVatRate(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
      </TwoCol>
      <ResultCard
        label="מע״מ"
        value={`₪${formatMoney2(vat)}`}
        lines={[`סכום כולל מע״מ: ₪${formatMoney2(total)}`]}
      />
    </div>
  );
}

function RentalTaxCalculator() {
  const [monthlyRent, setMonthlyRent] = useState(6000);
  const [taxRate, setTaxRate] = useState(10);

  const yearlyRent = monthlyRent * 12;
  const tax = yearlyRent * (taxRate / 100);
  const net = yearlyRent - tax;

  return (
    <div>
      <TitleBlock
        eyebrow="Built for better decisions"
        title="מס שכירות"
        subtitle="בדוק מס שנתי על הכנסה משכירות."
      />
      <TwoCol>
        <Field label="שכירות חודשית">
          <input
            type="number"
            value={monthlyRent}
            onChange={(e) => setMonthlyRent(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
        <Field label="אחוז מס (%)">
          <input
            type="number"
            value={taxRate}
            onChange={(e) => setTaxRate(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
      </TwoCol>
      <ResultCard
        label="מס שנתי"
        value={`₪${formatMoney(tax)}`}
        lines={[
          `שכירות שנתית: ₪${formatMoney(yearlyRent)}`,
          `נטו אחרי מס: ₪${formatMoney(net)}`,
        ]}
      />
    </div>
  );
}

function InflationCalculator() {
  const [amount, setAmount] = useState(100000);
  const [inflation, setInflation] = useState(3);
  const [years, setYears] = useState(10);

  const realValue = amount / Math.pow(1 + inflation / 100, years);

  return (
    <div>
      <TitleBlock
        eyebrow="Financial clarity starts here."
        title="מחשבון אינפלציה"
        subtitle="בדוק כמה ערך הכסף נשחק לאורך זמן."
      />
      <TwoCol>
        <Field label="סכום היום">
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
        <Field label="אינפלציה שנתית (%)">
          <input
            type="number"
            value={inflation}
            onChange={(e) => setInflation(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
        <Field label="מספר שנים">
          <input
            type="number"
            value={years}
            onChange={(e) => setYears(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
      </TwoCol>
      <ResultCard
        label="שווי ריאלי עתידי"
        value={`₪${formatMoney(realValue)}`}
        lines={[`ירידת ערך משוערת: ₪${formatMoney(amount - realValue)}`]}
      />
    </div>
  );
}

function GrossNetCalculator() {
  const [gross, setGross] = useState(20000);
  const [taxPercent, setTaxPercent] = useState(25);

  const tax = gross * (taxPercent / 100);
  const net = gross - tax;

  return (
    <div>
      <TitleBlock
        eyebrow="Built for better decisions"
        title="ברוטו נטו"
        subtitle="בדוק כמה באמת נשאר מהשכר."
      />
      <TwoCol>
        <Field label="שכר ברוטו">
          <input
            type="number"
            value={gross}
            onChange={(e) => setGross(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
        <Field label="אחוז ניכוי כולל (%)">
          <input
            type="number"
            value={taxPercent}
            onChange={(e) => setTaxPercent(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
      </TwoCol>
      <ResultCard
        label="שכר נטו משוער"
        value={`₪${formatMoney(net)}`}
        lines={[`ניכויים משוערים: ₪${formatMoney(tax)}`]}
      />
    </div>
  );
}

/* ================= פנסיה ================= */

function FireCalculator() {
  const [monthlyExpenses, setMonthlyExpenses] = useState(15000);
  const yearlyExpenses = monthlyExpenses * 12;
  const target = yearlyExpenses * 25;

  return (
    <div>
      <TitleBlock
        eyebrow="Financial clarity starts here."
        title="מחשבון FIRE"
        subtitle="בדוק כמה הון דרוש לחופש כלכלי."
      />
      <TwoCol>
        <Field label="הוצאות חודשיות">
          <input
            type="number"
            value={monthlyExpenses}
            onChange={(e) => setMonthlyExpenses(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
      </TwoCol>
      <ResultCard
        label="יעד חופש כלכלי"
        value={`₪${formatMoney(target)}`}
        lines={[
          `הוצאות שנתיות: ₪${formatMoney(yearlyExpenses)}`,
          "מבוסס על כלל 4%",
        ]}
      />
    </div>
  );
}

function MonthlyIncomeCapitalCalculator() {
  const [monthlyIncome, setMonthlyIncome] = useState(15000);
  const [withdrawRate, setWithdrawRate] = useState(4);

  const yearlyIncome = monthlyIncome * 12;
  const requiredCapital =
    withdrawRate > 0 ? yearlyIncome / (withdrawRate / 100) : 0;

  return (
    <div>
      <TitleBlock
        eyebrow="Built for better decisions"
        title="כמה הון צריך להכנסה חודשית"
        subtitle="חשב כמה הון דרוש כדי לייצר הכנסה חודשית קבועה."
      />
      <TwoCol>
        <Field label="הכנסה חודשית רצויה">
          <input
            type="number"
            value={monthlyIncome}
            onChange={(e) => setMonthlyIncome(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
        <Field label="אחוז משיכה שנתי (%)">
          <input
            type="number"
            value={withdrawRate}
            onChange={(e) => setWithdrawRate(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
      </TwoCol>
      <ResultCard
        label="הון נדרש"
        value={`₪${formatMoney(requiredCapital)}`}
        lines={[`הכנסה שנתית: ₪${formatMoney(yearlyIncome)}`]}
      />
    </div>
  );
}

function WithdrawalCalculator() {
  const [portfolio, setPortfolio] = useState(3000000);
  const [withdrawRate, setWithdrawRate] = useState(4);

  const yearly = portfolio * (withdrawRate / 100);
  const monthly = yearly / 12;

  return (
    <div>
      <TitleBlock
        eyebrow="Financial clarity starts here."
        title="משיכה חודשית מתיק"
        subtitle="בדוק כמה אפשר למשוך כל חודש מתיק השקעות."
      />
      <TwoCol>
        <Field label="שווי תיק">
          <input
            type="number"
            value={portfolio}
            onChange={(e) => setPortfolio(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
        <Field label="אחוז משיכה שנתי (%)">
          <input
            type="number"
            value={withdrawRate}
            onChange={(e) => setWithdrawRate(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
      </TwoCol>
      <ResultCard
        label="משיכה חודשית"
        value={`₪${formatMoney(monthly)}`}
        lines={[`משיכה שנתית: ₪${formatMoney(yearly)}`]}
      />
    </div>
  );
}

function PensionFutureCalculator() {
  const [current, setCurrent] = useState(300000);
  const [monthly, setMonthly] = useState(2500);
  const [returnRate, setReturnRate] = useState(6);
  const [years, setYears] = useState(25);

  const months = years * 12;
  const monthlyRate = returnRate / 100 / 12;

  let total = current;
  for (let i = 0; i < months; i++) {
    total = total * (1 + monthlyRate) + monthly;
  }

  return (
    <div>
      <TitleBlock
        eyebrow="Built for better decisions"
        title="פנסיה עתידית"
        subtitle="גלה כמה כסף יכול להיות לך בעתיד בחיסכון ארוך טווח."
      />
      <TwoCol>
        <Field label="סכום נוכחי">
          <input
            type="number"
            value={current}
            onChange={(e) => setCurrent(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
        <Field label="הפקדה חודשית">
          <input
            type="number"
            value={monthly}
            onChange={(e) => setMonthly(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
        <Field label="תשואה שנתית (%)">
          <input
            type="number"
            value={returnRate}
            onChange={(e) => setReturnRate(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
        <Field label="מספר שנים">
          <input
            type="number"
            value={years}
            onChange={(e) => setYears(Number(e.target.value))}
            style={inputStyle}
          />
        </Field>
      </TwoCol>
      <ResultCard label="סכום עתידי" value={`₪${formatMoney(total)}`} />
    </div>
  );
}

export default function App() {
  const [page, setPage] = useState<Page>("home");

  const categories = useMemo(
    () => ({
      capital: [
        {
          title: "ריבית דריבית",
          desc: "בדוק איך כסף גדל לאורך זמן",
          page: "compound" as Page,
        },
        {
          title: "DCA",
          desc: "השקעה חודשית קבועה",
          page: "dca" as Page,
        },
        {
          title: "תשואה",
          desc: "חשב רווח באחוזים",
          page: "return" as Page,
        },
        {
          title: "CAGR",
          desc: "תשואה שנתית ממוצעת",
          page: "cagr" as Page,
        },
        {
          title: "כמה יהיה לי בעתיד",
          desc: "חישוב שווי עתידי",
          page: "future-value" as Page,
        },
        {
          title: "כמה צריך לחסוך ליעד",
          desc: "הפקדה חודשית ליעד",
          page: "goal" as Page,
        },
        {
          title: "כמה זמן עד מיליון",
          desc: "זמן משוער להון יעד",
          page: "million" as Page,
        },
        {
          title: "מחשבון דיבידנד",
          desc: "הכנסה שנתית וחודשית",
          page: "dividend" as Page,
        },
        {
          title: "מס רווחי הון",
          desc: "כמה מס תשלם",
          page: "capital-gains-tax" as Page,
        },
      ],
      realEstate: [
        {
          title: "משכנתא",
          desc: "חשב החזר חודשי",
          page: "mortgage" as Page,
        },
        {
          title: "הלוואה",
          desc: "חישוב החזר הלוואה",
          page: "loan" as Page,
        },
        {
          title: "תשואה על דירה",
          desc: "בדוק תשואה שנתית",
          page: "rental-yield" as Page,
        },
        {
          title: "תזרים שכירות",
          desc: "כמה נשאר כל חודש",
          page: "cashflow" as Page,
        },
        {
          title: "הון עצמי נדרש",
          desc: "כמה צריך להביא",
          page: "downpayment" as Page,
        },
        {
          title: "שכירות מול קנייה",
          desc: "השוואת תשלום חודשי",
          page: "rent-vs-buy" as Page,
        },
        {
          title: "מס רכישה",
          desc: "חישוב מהיר",
          page: "purchase-tax" as Page,
        },
      ],
      taxes: [
        {
          title: "מע״מ",
          desc: "סכום לפני ואחרי מע״מ",
          page: "vat" as Page,
        },
        {
          title: "מס שכירות",
          desc: "כמה מס על שכירות",
          page: "rental-tax" as Page,
        },
        {
          title: "אינפלציה",
          desc: "שחיקת ערך הכסף",
          page: "inflation" as Page,
        },
        {
          title: "ברוטו נטו",
          desc: "כמה באמת נשאר",
          page: "gross-net" as Page,
        },
      ],
      pension: [
        {
          title: "FIRE",
          desc: "יעד חופש כלכלי",
          page: "fire" as Page,
        },
        {
          title: "כמה הון צריך להכנסה חודשית",
          desc: "כמה הון דרוש",
          page: "monthly-income-capital" as Page,
        },
        {
          title: "משיכה חודשית מתיק",
          desc: "כמה אפשר למשוך",
          page: "withdrawal" as Page,
        },
        {
          title: "פנסיה עתידית",
          desc: "חיסכון עתידי",
          page: "pension-future" as Page,
        },
      ],
    }),
    []
  );

  if (page === "home") {
    return (
      <div style={pageWrap}>
        <div style={containerStyle}>
          <div
            style={{
              ...shellCardStyle,
              padding: "90px 36px",
              textAlign: "center",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: -120,
                left: -120,
                width: 320,
                height: 320,
                borderRadius: "50%",
                background:
                  "radial-gradient(circle, rgba(37,99,235,0.18) 0%, rgba(37,99,235,0) 70%)",
                pointerEvents: "none",
              }}
            />
            <div
              style={{
                position: "absolute",
                bottom: -140,
                right: -120,
                width: 360,
                height: 360,
                borderRadius: "50%",
                background:
                  "radial-gradient(circle, rgba(59,130,246,0.14) 0%, rgba(59,130,246,0) 70%)",
                pointerEvents: "none",
              }}
            />

            <div
              style={{
                position: "relative",
                zIndex: 1,
              }}
            >
              <div
                style={{
                  fontSize: 16,
                  color: palette.subtext,
                  marginBottom: 12,
                }}
              >
                Financial clarity starts here.
              </div>

              <div
                style={{
                  fontSize: 76,
                  fontWeight: 900,
                  marginBottom: 18,
                  letterSpacing: "-0.06em",
                  color: "#0f172a",
                }}
              >
                FinCalc
              </div>

              <div
                style={{
                  fontSize: 22,
                  color: palette.subtext,
                  marginBottom: 34,
                  lineHeight: 1.8,
                }}
              >
                מחשבונים פיננסיים לשוק ההון, נדל״ן, מיסים ופנסיה.
              </div>

              <button
                onClick={() => setPage("categories")}
                style={{
                  padding: "16px 38px",
                  borderRadius: 999,
                  border: "none",
                  background: palette.primary,
                  color: "white",
                  fontSize: 17,
                  fontWeight: 700,
                  cursor: "pointer",
                  boxShadow: "0 12px 30px rgba(37,99,235,0.25)",
                }}
              >
                התחל לחשב
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (page === "categories") {
    return (
      <CategoryPage
        title="בחר קטגוריה"
        subtitle="כל הכלים החשובים במקום אחד."
        backPage="home"
        setPage={setPage}
        items={[
          {
            title: "נדל״ן",
            desc: "משכנתא, תשואה ועלויות רכישה",
            page: "real-estate",
          },
          {
            title: "שוק ההון",
            desc: "תשואות, ריבית דריבית והשקעות",
            page: "capital-market",
          },
          {
            title: "פנסיה",
            desc: "קצבה עתידית וחופש כלכלי",
            page: "pension",
          },
          {
            title: "מיסים",
            desc: "מע״מ, מס רווחי הון ושכר",
            page: "taxes",
          },
        ]}
      />
    );
  }

  if (page === "capital-market") {
    return (
      <CategoryPage
        title="שוק ההון"
        subtitle="מחשבונים להשקעות, תשואות וצמיחת הון."
        backPage="categories"
        setPage={setPage}
        items={categories.capital}
      />
    );
  }

  if (page === "real-estate") {
    return (
      <CategoryPage
        title="נדל״ן"
        subtitle="משכנתא, נכסים, תשואה והוצאות רכישה."
        backPage="categories"
        setPage={setPage}
        items={categories.realEstate}
      />
    );
  }

  if (page === "taxes") {
    return (
      <CategoryPage
        title="מיסים"
        subtitle="כלים מהירים לחישובי מס בסיסיים."
        backPage="categories"
        setPage={setPage}
        items={categories.taxes}
      />
    );
  }

  if (page === "pension") {
    return (
      <CategoryPage
        title="פנסיה וחופש כלכלי"
        subtitle="חישובים חשובים לעתיד הכלכלי שלך."
        backPage="categories"
        setPage={setPage}
        items={categories.pension}
      />
    );
  }

  if (page === "compound") {
    return (
      <PageShell onBack={() => setPage("capital-market")}>
        <CompoundCalculator />
      </PageShell>
    );
  }

  if (page === "dca") {
    return (
      <PageShell onBack={() => setPage("capital-market")}>
        <DcaCalculator />
      </PageShell>
    );
  }

  if (page === "return") {
    return (
      <PageShell onBack={() => setPage("capital-market")}>
        <ReturnCalculator />
      </PageShell>
    );
  }

  if (page === "cagr") {
    return (
      <PageShell onBack={() => setPage("capital-market")}>
        <CagrCalculator />
      </PageShell>
    );
  }

  if (page === "future-value") {
    return (
      <PageShell onBack={() => setPage("capital-market")}>
        <FutureValueCalculator />
      </PageShell>
    );
  }

  if (page === "goal") {
    return (
      <PageShell onBack={() => setPage("capital-market")}>
        <GoalCalculator />
      </PageShell>
    );
  }

  if (page === "million") {
    return (
      <PageShell onBack={() => setPage("capital-market")}>
        <MillionCalculator />
      </PageShell>
    );
  }

  if (page === "dividend") {
    return (
      <PageShell onBack={() => setPage("capital-market")}>
        <DividendCalculator />
      </PageShell>
    );
  }

  if (page === "capital-gains-tax") {
    return (
      <PageShell onBack={() => setPage("capital-market")}>
        <CapitalGainsTaxCalculator />
      </PageShell>
    );
  }

  if (page === "mortgage") {
    return (
      <PageShell onBack={() => setPage("real-estate")}>
        <MortgageCalculator />
      </PageShell>
    );
  }

  if (page === "loan") {
    return (
      <PageShell onBack={() => setPage("real-estate")}>
        <LoanCalculator />
      </PageShell>
    );
  }

  if (page === "rental-yield") {
    return (
      <PageShell onBack={() => setPage("real-estate")}>
        <RentalYieldCalculator />
      </PageShell>
    );
  }

  if (page === "cashflow") {
    return (
      <PageShell onBack={() => setPage("real-estate")}>
        <CashflowCalculator />
      </PageShell>
    );
  }

  if (page === "downpayment") {
    return (
      <PageShell onBack={() => setPage("real-estate")}>
        <DownpaymentCalculator />
      </PageShell>
    );
  }

  if (page === "rent-vs-buy") {
    return (
      <PageShell onBack={() => setPage("real-estate")}>
        <RentVsBuyCalculator />
      </PageShell>
    );
  }

  if (page === "purchase-tax") {
    return (
      <PageShell onBack={() => setPage("real-estate")}>
        <PurchaseTaxCalculator />
      </PageShell>
    );
  }

  if (page === "vat") {
    return (
      <PageShell onBack={() => setPage("taxes")}>
        <VatCalculator />
      </PageShell>
    );
  }

  if (page === "rental-tax") {
    return (
      <PageShell onBack={() => setPage("taxes")}>
        <RentalTaxCalculator />
      </PageShell>
    );
  }

  if (page === "inflation") {
    return (
      <PageShell onBack={() => setPage("taxes")}>
        <InflationCalculator />
      </PageShell>
    );
  }

  if (page === "gross-net") {
    return (
      <PageShell onBack={() => setPage("taxes")}>
        <GrossNetCalculator />
      </PageShell>
    );
  }

  if (page === "fire") {
    return (
      <PageShell onBack={() => setPage("pension")}>
        <FireCalculator />
      </PageShell>
    );
  }

  if (page === "monthly-income-capital") {
    return (
      <PageShell onBack={() => setPage("pension")}>
        <MonthlyIncomeCapitalCalculator />
      </PageShell>
    );
  }

  if (page === "withdrawal") {
    return (
      <PageShell onBack={() => setPage("pension")}>
        <WithdrawalCalculator />
      </PageShell>
    );
  }

  if (page === "pension-future") {
    return (
      <PageShell onBack={() => setPage("pension")}>
        <PensionFutureCalculator />
      </PageShell>
    );
  }

  return null;
}
